import { Autorenew } from "@mui/icons-material";
import { alpha, Box, Card, CardContent, CardHeader, Skeleton, useTheme } from "@mui/material";
import { useMemo } from "react";
import Chart from "react-apexcharts";
import { getAdjustedColorForContrast } from "../../app/utils/utilityFunctions";
import { UiIconButton } from "../../components/ui/UiUtils";

const getChartOptions = (theme, labels, colors) => {
  return {
    chart: {
      background: "transparent",
      type: "donut",
    },
    colors,
    labels,
    stroke: { width: 0 },
    plotOptions: {
      pie: {
        donut: {
          size: "75%",
          labels: {
            show: true,
            total: {
              show: true,
              fontSize: "22px",
              fontWeight: 700,
              color: theme.palette.text.secondary,
            },
          },
        },
      },
    },
    dataLabels: { enabled: false },
    legend: { show: false },
    states: {
      active: {
        filter: {
          type: "none",
        },
      },
      hover: {
        filter: {
          type: "none",
        },
      },
    },
    theme: { mode: theme.palette.mode },
    tooltip: { fillSeriesColor: false },
  };
};

const DonutChartSkeleton = () => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        position: "relative",
        width: 320,
        height: 320,
        margin: "auto",
      }}
    >
      <Skeleton
        variant="circular"
        width={320}
        height={320}
        animation="wave"
        sx={{
          backgroundColor: alpha(theme.palette.primary.main, 0.1),
        }}
      />
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 200,
          height: 200,
          borderRadius: "50%",
          backgroundColor: theme.palette.background.paper,
        }}
      />
    </Box>
  );
};

export default function TasksDonutChart({ refetchData, data, loading }) {
  const theme = useTheme();

  const chartData = useMemo(() => {
    const originalData = data?.data?.pieChart;
    if (!originalData) {
      return {
        chartSeries: [],
        colors: Array(9)
          .fill(["primary", "secondary", "warning", "error", "info"][Math.floor(Math.random(5))])
          .map((color) => theme.palette[color].main),
        labels: Array(9).fill("Error"),
      };
    }

    return {
      chartSeries: originalData.series,
      colors: originalData.labels.map(({ color }) => getAdjustedColorForContrast(color, theme)[0]),
      labels: originalData.labels.map(({ label }) => label),
    };
  }, [theme, data]);

  return (
    <Card
      sx={{
        p: 3,
        height: "100%",
        borderRadius: 4,
        border: "1px solid",
        borderColor: theme.palette.primary.sectionBorder600,
      }}
    >
      <CardHeader
        title="Tasks Distribution Report"
        sx={{ pb: 0 }}
        action={
          <UiIconButton title="Refetch Data" onClickIcon={refetchData} disabled={loading}>
            <Autorenew />
          </UiIconButton>
        }
      />
      <CardContent>
        {loading ? (
          <DonutChartSkeleton />
        ) : (
          <Chart
            height={350}
            options={getChartOptions(theme, chartData.labels, chartData.colors)}
            series={chartData.chartSeries}
            width="100%"
            type="donut"
          />
        )}
      </CardContent>
    </Card>
  );
}
